import * as React from 'react';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';

import { ActionsContainer } from '@/atoms/CommonLayoutContainers/ActionsContainer';
import { AvatarContainer } from '@/atoms/CommonLayoutContainers/AvatarContainer';
import { ContentContainer } from '@/atoms/CommonLayoutContainers/ContentContainer';
import { HeaderContainer } from '@/atoms/CommonLayoutContainers/HeaderContainer';
import { SecondaryHeader } from '@/atoms/CommonLayoutContainers/SecondaryHeader';
import type { ClickHandlerArgs } from '@/components/SourceCards/hooks/useFeedCard';
import { canClickTarget } from '@/utils/canClickTarget';

export type FeedCardType = {
  /**
   * card id
   */
  id?: string;
  /**
   * card selected in overlay
   */
  active?: boolean;
  /**
   * card status
   */
  read?: boolean;
  /**
   * Card source icon or any other
   */
  cardAvatar?: React.ReactNode;
  /**
   * Card title component
   */
  cardTitle: React.ReactNode;
  /**
   * Card primary header component
   */
  cardPrimaryHeader?: React.ReactNode;
  /**
   * Card content component
   */
  cardContent: React.ReactNode;
  /**
   * Card action component
   */
  cardAction?: React.ReactNode;
  /**
   * Card RSS mobile title
   */
  rssMobileTitle?: React.ReactNode;
  /**
   * feed click handler
   */
  onClickHandler?: (args?: ClickHandlerArgs) => void;
  /**
   * card selected for deletion
   */
  selected?: boolean;
};

export const CardHighlightedStyle = css<Pick<FeedCardType, 'active'>>`
  ::before {
    ${({ active }) => (active ? tw`opacity-100` : tw`opacity-0`)}
    ${tw`absolute left-0 top-0 bottom-0 w-1 transition-opacity duration-150 ease-out`}
  box-shadow: 4px 0px 0px var(--secondary-green-900) inset;
    content: '';
  }
`;

const Card = styled.div<Pick<FeedCardType, 'active' | 'read' | 'selected'>>`
  ${tw`pr-6 py-3.5 pl-4 relative cursor-pointer w-full hover:bg-gray-100 transition-colors duration-200 ease-out`}
  ${({ active }) => active && tw`bg-gray-100`}
  ${({ selected }) => selected && tw`bg-gray-200`}
  ${({ read }) => (read ? tw`bg-gray-100` : tw` hover:bg-gray-100 `)}
  ${CardHighlightedStyle}
`;

export const FeedCard: React.ForwardRefExoticComponent<
  FeedCardType & React.RefAttributes<HTMLDivElement>
> = React.forwardRef(
  (
    {
      active,
      read = false,
      cardAction,
      cardAvatar,
      cardContent,
      cardTitle,
      cardPrimaryHeader,
      rssMobileTitle,
      onClickHandler,
      selected,
      id,
    },
    ref
  ) => {
    const onClick = (e: React.MouseEvent) => {
      if (canClickTarget(e.target as HTMLElement)) {
        onClickHandler?.();
      }
    };

    return (
      <Card
        active={active}
        ref={ref}
        role='presentation'
        data-feedcard
        onClick={onClick}
        selected={selected}
        read={read}
        id={id}
      >
        <div className='grid grid-cols-feed-card sm:grid-rows-sm-feed-card gap-y-1.5 gap-x-3'>
          {cardAvatar ? <AvatarContainer>{cardAvatar}</AvatarContainer> : null}
          <HeaderContainer startFirstColumn={!cardAvatar}>
            <div className='overflow-hidden self-center'>{cardTitle}</div>
            <SecondaryHeader>{cardPrimaryHeader}</SecondaryHeader>
          </HeaderContainer>
          <ContentContainer overflowHidden startFirstColumn={!cardAvatar}>
            {rssMobileTitle ? (
              <div className='mb-2'>{rssMobileTitle}</div>
            ) : null}
            {cardContent}
          </ContentContainer>
          {cardAction ? (
            <ActionsContainer startFirstColumn={!cardAvatar}>
              {cardAction}
            </ActionsContainer>
          ) : null}
        </div>
      </Card>
    );
  }
);
